/* eslint-disable no-unused-vars, warp-drive/no-legacy-request-patterns */
import ProgressionJudgement from '@linc-technologies/linced-common/enums/progression-judgement';
import {
  byKeys,
  filterBy,
  findBy,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';
import reportStudentName from '@linc-technologies/linced-common/utils/report-student-name';

// Rubric resolution lives in linced-common/utils/rubrics; re-exported so
// existing imports from this module keep working.
export {
  resolveRubric,
  resolveRubricElements,
} from '@linc-technologies/linced-common/utils/rubrics';

const PRONOUNS = {
  M: {
    subject: 'he',
    object: 'him',
    possessive: 'his',
    possessivePronoun: 'his',
    reflexive: 'himself',
  },
  F: {
    subject: 'she',
    object: 'her',
    possessive: 'her',
    possessivePronoun: 'hers',
    reflexive: 'herself',
  },
};

const NEUTRAL = {
  subject: 'they',
  object: 'them',
  possessive: 'their',
  possessivePronoun: 'theirs',
  reflexive: 'themselves',
};

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function replaceWord(text, from, to) {
  return text.replace(new RegExp('\\b' + escapeRegex(from) + '\\b', 'g'), to);
}

export function replaceNameAndPronouns(html, fromStudent, toStudent, template) {
  // Replace first name, as the template shows it for each student
  let fromFirst = reportStudentName(fromStudent, template).firstName;
  let toFirst = reportStudentName(toStudent, template).firstName;
  let result = html;
  if (fromFirst) {
    result = replaceWord(result, fromFirst, toFirst);
  }

  // Replace pronouns
  let fromGender = fromStudent.get('preferredGender');
  let toGender = toStudent.get('preferredGender');
  let fromP = PRONOUNS[fromGender] || NEUTRAL;
  let toP = PRONOUNS[toGender] || NEUTRAL;

  if (fromP !== toP) {
    // Use placeholders to avoid double-replacement when the same word
    // serves multiple roles (e.g. "her" is both possessive and object).
    let placeholders = {};
    let idx = 0;

    var replaceWithPlaceholder = function (text, from, to) {
      let placeholder = '\x00PH' + idx++ + '\x00';
      placeholders[placeholder] = to;
      return text.replace(
        new RegExp('\\b' + escapeRegex(from) + '\\b', 'g'),
        placeholder,
      );
    };

    // Order: longer/more specific first to avoid partial matches
    // Reflexive (himself/herself/themselves)
    result = replaceWithPlaceholder(result, fromP.reflexive, toP.reflexive);
    result = replaceWithPlaceholder(
      result,
      fromP.reflexive.charAt(0).toUpperCase() + fromP.reflexive.slice(1),
      toP.reflexive.charAt(0).toUpperCase() + toP.reflexive.slice(1),
    );

    // Possessive pronoun standalone (hers/theirs — not "his" which overlaps)
    if (fromP.possessivePronoun !== fromP.possessive) {
      result = replaceWithPlaceholder(
        result,
        fromP.possessivePronoun,
        toP.possessivePronoun,
      );
      result = replaceWithPlaceholder(
        result,
        fromP.possessivePronoun.charAt(0).toUpperCase() +
          fromP.possessivePronoun.slice(1),
        toP.possessivePronoun.charAt(0).toUpperCase() +
          toP.possessivePronoun.slice(1),
      );
    }

    // Object before possessive so "her" as object is matched before
    // "her" as possessive (disambiguate by context: "her" followed by
    // a word boundary + letter is possessive, otherwise object).
    // Since true disambiguation is hard, we handle possessive first
    // only when it differs from object (M and N); for F they are the
    // same word so we just replace once with the possessive form and
    // accept the approximation.
    if (fromP.possessive !== fromP.object) {
      // Possessive (his/their)
      result = replaceWithPlaceholder(result, fromP.possessive, toP.possessive);
      result = replaceWithPlaceholder(
        result,
        fromP.possessive.charAt(0).toUpperCase() + fromP.possessive.slice(1),
        toP.possessive.charAt(0).toUpperCase() + toP.possessive.slice(1),
      );
      // Object (him/them)
      result = replaceWithPlaceholder(result, fromP.object, toP.object);
      result = replaceWithPlaceholder(
        result,
        fromP.object.charAt(0).toUpperCase() + fromP.object.slice(1),
        toP.object.charAt(0).toUpperCase() + toP.object.slice(1),
      );
    } else {
      // "her" is both possessive and object — use context:
      // "her" followed by a letter/word = possessive (her book)
      // "her" at end or followed by punctuation = object (told her)
      result = result.replace(
        new RegExp(
          '\\b' + escapeRegex(fromP.possessive) + '(?=\\s+[a-z])',
          'gi',
        ),
        function (match) {
          let placeholder = '\x00PH' + idx++ + '\x00';
          let isUpper =
            match.charAt(0) === match.charAt(0).toUpperCase() &&
            match.charAt(0) !== match.charAt(0).toLowerCase();
          placeholders[placeholder] = isUpper
            ? toP.possessive.charAt(0).toUpperCase() + toP.possessive.slice(1)
            : toP.possessive;
          return placeholder;
        },
      );
      result = result.replace(
        new RegExp('\\b' + escapeRegex(fromP.object) + '\\b', 'gi'),
        function (match) {
          let placeholder = '\x00PH' + idx++ + '\x00';
          let isUpper =
            match.charAt(0) === match.charAt(0).toUpperCase() &&
            match.charAt(0) !== match.charAt(0).toLowerCase();
          placeholders[placeholder] = isUpper
            ? toP.object.charAt(0).toUpperCase() + toP.object.slice(1)
            : toP.object;
          return placeholder;
        },
      );
    }

    // Subject (he/she/they)
    result = replaceWithPlaceholder(result, fromP.subject, toP.subject);
    result = replaceWithPlaceholder(
      result,
      fromP.subject.charAt(0).toUpperCase() + fromP.subject.slice(1),
      toP.subject.charAt(0).toUpperCase() + toP.subject.slice(1),
    );

    // Restore placeholders
    Object.keys(placeholders).forEach(function (ph) {
      result = result.split(ph).join(placeholders[ph]);
    });
  }

  return result;
}

/**
 * Maps report component types to their corresponding
 * student-report-section field names.
 */
export const TEXT_COMPONENT_TYPES = Object.freeze([
  'teacher-comment',
  'next-steps',
  'help-at-home',
]);

export const PROGRESS_COMPONENT_TYPES = Object.freeze([
  'effort',
  'strand-visual',
  'progress-descriptor',
]);

export const PRINCIPAL_COMMENT_TEXT_TYPES = Object.freeze([
  'principal-comment',
]);

export const REVIEW_EDITABLE_TYPES = Object.freeze([
  'teacher-comment',
  'next-steps',
  'help-at-home',
  'principal-comment',
  'progress-visual',
  'strand-visual',
  'effort',
  'progress-descriptor',
]);

export const COMPONENT_FIELD_MAP = Object.freeze({
  'teacher-comment': 'narrative',
  'next-steps': 'nextSteps',
  'help-at-home': 'homeHelp',
  'principal-comment': 'narrative',
  // Not a template component: the goals half of the next-steps block, which
  // the write views edit separately from the rubric / teacher next steps.
  'next-steps-goals': 'nextStepsGoals',
});

/**
 * Maps student-report-section text fields to the flag recording that the
 * field still holds untouched rubric-generated text.
 */
export const AUTO_GENERATED_FLAG_MAP = Object.freeze({
  narrative: 'narrativeAutoGenerated',
  nextSteps: 'nextStepsAutoGenerated',
  homeHelp: 'homeHelpAutoGenerated',
});

/**
 * Properties to set when a person (not the rubric) changes a text field:
 * the new value, plus clearing its auto-generated flag so slider changes
 * stop regenerating it.
 */
export function manualEditProperties(field, value) {
  let props = { [field]: value };
  let autoGeneratedFlag = AUTO_GENERATED_FLAG_MAP[field];
  if (autoGeneratedFlag) {
    props[autoGeneratedFlag] = false;
  }
  return props;
}

// True when html has visible text once tags are stripped.
export function hasText(html) {
  return !!html && html.replace(/<[^>]*>/g, '').trim().length > 0;
}

// An HTML fragment's text for comparing items: tags stripped, non-breaking
// spaces and runs of whitespace collapsed, case ignored.
function comparableText(html) {
  return String(html || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * The texts of html's list items, for checking whether an item is already
 * listed regardless of attributes, inner markup or whitespace.
 *
 * @param {string} html
 * @return {Set<string>}
 */
export function listItemTexts(html) {
  let texts = new Set();
  let pattern = /<li\b[^>]*>([\s\S]*?)<\/li>/gi;
  let match;
  while ((match = pattern.exec(String(html || '')))) {
    texts.add(comparableText(match[1]));
  }
  return texts;
}

/**
 * Whether a list item with text html's text is already in listedTexts
 * (from listItemTexts).
 */
export function isListed(html, listedTexts) {
  return listedTexts.has(comparableText(html));
}

/**
 * The text a component shows for a section. Next steps combines the goals
 * and rubric / teacher-written halves, as rendered on the report.
 */
export function sectionComponentText(section, componentType) {
  if (!section) return '';
  if (componentType === 'next-steps') {
    return section.get('nextStepsDisplay') || '';
  }
  let field = COMPONENT_FIELD_MAP[componentType];
  return (field && section.get(field)) || '';
}

/**
 * Parse a section configuration that may be a JSON string or already-parsed object.
 */
export function parseConfig(config) {
  if (!config) return { components: [] };
  if (typeof config === 'string') {
    try {
      return JSON.parse(config);
    } catch (e) {
      return { components: [] };
    }
  }
  return config;
}

/**
 * Returns a report template with its sections loaded, fetching them only when
 * the store does not already hold them.
 *
 * Two cache traps make the obvious spellings silently wrong, and both produce
 * the same symptom: a report renders "No sections yet" because
 * ReportView#resolvedSections filters peekAll and finds nothing.
 *
 *  - `peekRecord(id) || findRecord(id, {includeSections})` never fetches when
 *    the record is cached, and a cached record is NOT proof its sections came
 *    with it. The template gets cached section-less by anything that loads it
 *    plainly — resolveCurrentTemplate does exactly that for every template a
 *    learner's reports reference.
 *  - `findRecord(id, {includeSections})` without `reload: true` short-circuits
 *    on that same cached record, so the adapter option is never applied.
 *
 * Active templates hide both: the reporting route loads them with
 * includeSections up front, so their sections are always present. Inactive and
 * soft-deleted templates are not in that query and are the ones that break.
 */
export async function loadTemplateWithSections(store, templateId) {
  if (!templateId) return null;
  let template = store.peekRecord('reports/report-template', templateId);
  let hasSections =
    !!template &&
    filterBy(
      store.peekAll('reports/report-template-section'),
      'reportTemplateId.id',
      templateId,
    ).length > 0;
  if (template && hasSections) {
    return template;
  }
  return store.findRecord('reports/report-template', templateId, {
    reload: true,
    adapterOptions: { includeSections: true },
  });
}

const MONTH_ABBR = [
  '',
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// Parsed from the string rather than through Date, which would shift the day
// across a timezone boundary — these are calendar dates, not instants.
function parseIsoDate(value) {
  if (typeof value !== 'string') return null;
  let match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  let month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return { year: Number(match[1]), month, day: Number(match[3]) };
}

function formatBound(date, withYear) {
  let formatted = `${MONTH_ABBR[date.month]} ${date.day}`;
  return withYear ? `${formatted}, ${date.year}` : formatted;
}

/**
 * Formats a period's backend-resolved bounds as "Nov 20, 2025 - Feb 12, 2026".
 *
 * Takes resolved dates rather than a template and an index because a period
 * carries only a month and a day: placing it on a real date needs the school's
 * rollover, so only the server can say which calendar year a period ends in.
 * Guessing that on the client is what produced "Jan - Jan 28" for templates
 * whose periods cross the year.
 *
 * The year appears on both bounds when the range spans two calendar years —
 * the case that was previously unrenderable — and is omitted otherwise, where
 * it would only be noise.
 *
 * Returns '' when either bound is missing. Only the period in progress carries
 * resolved bounds today, so callers showing past periods must be able to fall
 * back to the label alone.
 */
export function formatPeriodRange(startDate, endDate) {
  let start = parseIsoDate(startDate);
  let end = parseIsoDate(endDate);
  if (!start || !end) return '';

  let spansYears = start.year !== end.year;
  return `${formatBound(start, spansYears)} - ${formatBound(end, spansYears)}`;
}

/**
 * Resolve a period's label ("Mid Year", "Term 4", …) for a stored periodIndex.
 *
 * Prefers the label on a counts entry, because the backend resolves labels
 * there and can recover one for an index orphaned by a later template version
 * — a lookup against the template alone cannot. The entry is ignored unless
 * its periodIndex matches the one being named: entries describe one period,
 * and naming a report after a different one is worse than not naming it.
 *
 * The fallback indexes the template's BY-MONTH-SORTED periods, which is the
 * index space periodIndex is stamped against, so the sort here must match. It
 * is a lookup, not a calculation: it never decides WHICH period is current,
 * only what the one being shown is called.
 *
 * Returns '' when the index no longer resolves. Callers should treat that as
 * "unknown" rather than rendering a placeholder.
 */
export function periodLabelAt(template, periodIndex, entry) {
  if (!template) return '';
  if (entry && entry.periodIndex === periodIndex && entry.periodLabel) {
    return entry.periodLabel;
  }
  let periods = template.get ? template.get('periods') : template.periods;
  let sorted = periods ? sortBy(periods, 'month') : [];
  let period = sorted[periodIndex];
  if (!period) return '';
  return (period.get ? period.get('label') : period.label) || '';
}

/**
 * Build the display name for a template at a given reporting period, e.g.
 * "2026 Three periods Mid Year".
 *
 * The label comes from the counts entry when that entry describes this very
 * period — the backend resolves labels there, and can recover one for an index
 * orphaned by a later template version. Otherwise it falls back to looking the
 * index up in the template's by-month-sorted periods, which is the same index
 * space periodIndex is stamped against. That fallback is a lookup, not a
 * calculation: it never decides WHICH period is current, only what the one
 * being shown is called.
 *
 * `entry` is optional and ignored unless its periodIndex matches — a historic
 * period must not be named after the current one.
 *
 * periodLabelAt resolves just the label; templateDisplayName wraps it with the
 * year and template name.
 */
export function templateDisplayName(template, periodIndex, year, entry) {
  if (!template) return '';
  let label = periodLabelAt(template, periodIndex, entry);
  let name = `${year} ${template.get('name')}`;
  return label ? `${name} ${label}` : name;
}

/**
 * Resolve an array of IDs to records from an options array.
 */
export function resolveIds(ids, options) {
  if (!ids || !ids.length || !options) return [];
  return ids.map((id) => findBy(options, 'id', id)).filter(Boolean);
}

/**
 * Resolve the correct levelId for a progression based on judgement + expectations.
 *
 * @param {number} judgement       - 1-5 (Well Below → Well Above)
 * @param {string} goalSubjectId   - subject the progression belongs to
 * @param {string} milestoneId     - milestone the progression targets
 * @param {Object} store           - Ember Data store (uses peekAll)
 * @returns {string|null} the level id, or null if it cannot be determined
 */
export function resolveLevelId(judgement, goalSubjectId, milestoneId, store) {
  if (!goalSubjectId || !milestoneId) {
    return null;
  }

  let exp = store
    .peekAll('milestones/expectation')
    .find(
      (expectation) =>
        expectation.get('goal_subject_id') === goalSubjectId &&
        expectation.get('milestone_id') === milestoneId,
    );
  if (!exp) {
    return null;
  }

  let index;
  switch (Number(judgement)) {
    case ProgressionJudgement.WELL_BELOW:
      index = exp.get('belowIndex') - 1;
      break;
    case ProgressionJudgement.BELOW:
      index = exp.get('belowIndex');
      break;
    case ProgressionJudgement.AT:
      index = exp.get('atIndex');
      break;
    case ProgressionJudgement.ABOVE:
      index = exp.get('aboveIndex');
      break;
    case ProgressionJudgement.WELL_ABOVE:
      index = exp.get('wellAboveIndex');
      break;
    default:
      return null;
  }
  if (index == null || index < -1) {
    return null;
  }

  let levels = store
    .peekAll('milestones/level')
    .filter((level) => level.get('goal_subject_id') === goalSubjectId)
    .sort(byKeys('sequence'));
  return levels[index + 1] ? levels[index + 1].get('id') : null;
}

export function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Pronoun keys rubric authors can use in criteria wording, mapped to the
// pronoun for each preferred gender (anything else gets the neutral one).
const RUBRIC_PRONOUN_KEYS = Object.freeze({
  'HE/SHE/THEY': { M: 'he', F: 'she', N: 'they' },
  'HIM/HER/THEM': { M: 'him', F: 'her', N: 'them' },
  'HIS/HER/THEIR': { M: 'his', F: 'her', N: 'their' },
});

const RUBRIC_KEY_PATTERN =
  /\bFIRSTNAME\b|\{name\}|\b(?:HE\/SHE\/THEY|HIM\/HER\/THEM|HIS\/HER\/THEIR)\b/g;

// True when a key at offset opens a sentence: nothing but tags and
// whitespace before it, or a full stop / ! / ? just before.
function startsSentence(text, offset) {
  let before = text.slice(0, offset).replace(/(?:<[^>]*>|&nbsp;|\s)+$/, '');
  return !before || /[.!?]$/.test(before);
}

/**
 * Personalise rubric wording for a student by filling in the rubric keys:
 * FIRSTNAME (or {name}, used in some older wording) becomes their first
 * name as the report template shows it, and HE/SHE/THEY, HIM/HER/THEM and HIS/HER/THEIR become the pronoun
 * for their preferred gender (capitalised at the start of a sentence).
 * Rubric text is stored HTML-encoded, so the name is escaped before it is
 * inserted.
 */
export function personaliseRubricText(text, student, template) {
  if (!text) {
    return '';
  }

  let gender = student && student.get('preferredGender');
  let firstName = reportStudentName(student, template).firstName;

  return String(text).replace(
    RUBRIC_KEY_PATTERN,
    function (key, offset, whole) {
      if (key === 'FIRSTNAME' || key === '{name}') {
        return firstName ? escapeHtml(firstName) : key;
      }
      let forms = RUBRIC_PRONOUN_KEYS[key];
      let pronoun = forms[gender] || forms.N;
      return startsSentence(whole, offset)
        ? pronoun.charAt(0).toUpperCase() + pronoun.slice(1)
        : pronoun;
    },
  );
}

// Returns a shuffled copy of items (Fisher-Yates).
export function shuffle(items) {
  let result = Array.from(items || []);
  for (let i = result.length - 1; i > 0; i--) {
    let j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

// Sections in flight, keyed by record, holding a promise that resolves
// once that section's most recently queued save has settled.
const pendingSectionSaves = new WeakMap();

// Chains section.save() onto any save already in flight for the same
// section, instead of firing a second concurrent one. Overlapping saves on
// a record with a fragmentArray (e.g. elementProgressions) can race and
// throw on commit, so every save site for reports/student-report-section
// must go through this (or saveSection below) rather than calling
// section.save() directly.
export function queueSectionSave(section) {
  if (!section || section.get('isDestroyed') || section.get('isDestroying')) {
    return Promise.resolve();
  }

  let doSave = () => {
    if (!section || section.get('isDestroyed') || section.get('isDestroying')) {
      return undefined;
    }
    return section.save();
  };

  let previous = pendingSectionSaves.get(section) || Promise.resolve();
  let current = previous.then(doSave, doSave);
  pendingSectionSaves.set(section, current);
  return current;
}

// Queued save that also reports failure via the toaster — the version most
// call sites want. Errors are swallowed here (toasted) rather than
// rejecting, since most callers fire this from a debounce and don't await it.
export function saveSection(section, toaster) {
  return queueSectionSave(section).catch(() => {
    if (toaster) {
      toaster.error('Failed to save report changes - your edits are not saved');
    }
  });
}
